/**
 * @voltxampere/ui — Probe overlay (Sprint 13 + Sprint 14)
 *
 * Sprint 13 shipped the `probe-overlay` SceneNode visual as a
 * `"0.0V"` placeholder. Sprint 14 swaps that for a `label`
 * argument the caller pre-formats from a live `DCResult`. When
 * no DC result is available we render an em-dash (`"—"`) — Kural
 * 9 mandates an honest "no value" rather than a stale zero.
 *
 * `formatVoltage` is the canonical formatter the inspector and
 * `probe-binding` effect both call so the overlay text matches
 * what the inspector reports for the same node.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export const PROBE_NO_VALUE = "—";
/**
 * Render a single voltage / current label overlay at world-space
 * `position`. Sprint 18 D1 adds the `intensity` argument so
 * transient-live readouts can switch to `theme.accent.brassHot`
 * (matching mockup zenit-platform.html line 1682's `+8.245V`
 * brass-hot). Static (DC OP) defaults to brass.
 *
 * Font choice tracks ZENIT_BRAND.md §6: small live numerics use
 * EB Garamond italic for the human-facing readout, JetBrains Mono
 * tabular-nums for the value digits. The mockup compresses both
 * into "italic 13px" — we replicate that here with EB Garamond
 * italic so the reading lines up with the inspector's measurement
 * tablosu (A4) and the rest of the brass-italic data display.
 */
export function drawProbeOverlay(ctx, position, theme, label = PROBE_NO_VALUE, intensity = "static") {
    ctx.save();
    ctx.fillStyle = intensity === "live" ? theme.accent.brassHot : theme.accent.brass;
    ctx.font = "italic 13px 'EB Garamond', Georgia, serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, position.x + 6, position.y);
    ctx.restore();
}
/**
 * Voltage formatter for probe overlays + inspector live readouts.
 * Picks SI prefix by absolute magnitude; result is always 3-4
 * significant digits, fits inside the inspector column without
 * line wrapping. Returns "—" for non-finite inputs (Kural 9).
 */
export function formatVoltage(volts) {
    if (!Number.isFinite(volts))
        return PROBE_NO_VALUE;
    const abs = Math.abs(volts);
    if (abs >= 1)
        return `${volts.toFixed(3)} V`;
    if (abs >= 1e-3)
        return `${(volts * 1e3).toFixed(2)} mV`;
    if (abs >= 1e-6)
        return `${(volts * 1e6).toFixed(0)} μV`;
    if (abs === 0)
        return "0.000 V";
    return `${(volts * 1e9).toFixed(0)} nV`;
}
//# sourceMappingURL=probe-overlay.js.map